import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { X, AlertCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  buildContent,
  type BloqueoFactura,
  type BloqueoFacturacionError,
} from '@/features/proveedores/lib/bloqueoFacturacion';
import { Badge } from '@/shared/components/Badge';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

export type { BloqueoFactura, BloqueoFacturacionError };

interface BloqueoFacturacionProps {
  open: boolean;
  error: BloqueoFacturacionError | null;
  onClose: () => void;
}

// Fecha de pago (YYYY-MM-DD del backend) → dd/mm/yyyy sin depender de la zona
// horaria (parseo manual, no new Date(str)).
const formatFechaPago = (fecha: string) => {
  const [y, m, d] = (fecha || '').split('-');
  if (!y || !m || !d) return fecha || '—';
  return `${d}/${m}/${y}`;
};


/**
 * Mensaje de bloqueo al intentar subir una factura. Recibe el objeto de error
 * 422 completo del backend y renderiza el mensaje + CTA según su `tipo`
 * (cfdi_complemento, expediente_mensual con 3 variantes, docs_financieros) o
 * un mensaje genérico para tipos desconocidos.
 */
const BloqueoFacturacion = ({ open, error, onClose }: BloqueoFacturacionProps) => {
  const navigate = useNavigate();
  if (!open || !error) return null;

  const content = buildContent(error);
  const facturas = content.facturas ?? [];

  return (
    <CapaDeModal zIndex={60}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {content.titulo ?? 'No puedes subir facturas'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 pt-4">
          <p className="text-sm text-gray-600 mb-4">{content.body}</p>

          {/* Lo que falta para desbloquearse (solo expediente_vencido). */}
          {(content.faltantes?.length ?? 0) > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Lo que falta
              </p>
              <ul className="mt-1 list-disc pl-5 space-y-0.5 text-sm text-gray-700">
                {content.faltantes!.map((texto) => (
                  <li key={texto}>{texto}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Lista de facturas (solo cfdi_complemento). Cada renglon lleva a los
              pagos de su orden, que es donde se sube el complemento: con
              facturas de varias ordenes el boton de abajo no puede llevar a una
              sola, y sin esto el proveedor tendria que buscarlas a mano. */}
          {facturas.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto mb-5">
              {facturas.map((f, i) => (
                <button
                  key={`${f.folio}-${i}`}
                  type="button"
                  disabled={!f.orden_id}
                  onClick={() => {
                    onClose();
                    navigate(`/proveedores/ordenes/${f.orden_id}/pagos`);
                  }}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 ${
                    f.orden_id ? 'hover:bg-gray-50 transition-colors' : 'cursor-default'
                  }`}
                >
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {f.folio ? `Folio ${f.folio}` : 'Factura sin folio'}
                    </p>
                    <p className="text-xs text-gray-500">Fecha de pago: {formatFechaPago(f.fecha_pago)}</p>
                  </div>
                  {typeof f.dias_habiles_vencidos === 'number' && f.dias_habiles_vencidos > 0 && (
                    <Badge color="rojo" className="flex-shrink-0">
                      {f.dias_habiles_vencidos} días hábiles vencidos
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Acciones. Los botones no se estiran a partes iguales: el de la
              accion ("Ir a Documentos del mes") necesita su ancho o el texto se
              parte en dos renglones. Cerrar se queda con lo justo y whitespace-nowrap
              cierra la puerta a que cualquiera de los dos se quiebre. */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={estiloDeBoton('secundario')}
            >
              Cerrar
            </button>
            {content.cta && (
              <button
                onClick={() => {
                  onClose();
                  navigate(content.cta!.to);
                }}
                className={estiloDeBoton('primario', 'flex-1')}
              >
                {content.cta.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </CapaDeModal>
  );
};

export default BloqueoFacturacion;
